import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { NgForm } from '@angular/forms';

import { Contact } from '../../../models/contact';
import { User } from '../../../models/user';

import { AuthService } from '../../../services/common/auth.service';
import { BrowserNotificationService } from '../../../services/common/browser-notification.service';
import { NotyService } from '../../../services/common/noty.service';
import { UserService } from '../../../services/users/user.service';
import { HttpClientService } from '../../../services/common/http-client.service';

const OTP_SOURCES = ['element', 'email', 'phone'];

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent implements OnInit {
  @ViewChild('signatureFileInput', { static: false }) signatureFileInput: ElementRef;

  creatingNewApiKey: boolean = false;

  currentUserGroups: any = {};

  currentUserId: number;

  extension: number;

  fields: any = {};

  formOptions: any = {
    calendar_agendas: [],
    entry_ages: [],
    groups: [],
    history_ages: [],
    intranets: [],
    managers: [],
    matter_history_types: [],
    time_zones: [],
    chart_of_accounts: [],
    termination_reasons: [],
  };

  formSubmitted: boolean = false;
  formSubmitting: boolean = false;

  loadingOtpSettings: boolean = false;

  otpSettings: any = {};

  otpSettingsSubmitted: boolean = false;

  person: Contact = new Contact();

  saving: boolean = false;

  updatingOtpSettings: boolean = false;

  uploadingSignature: boolean = false;

  user: User = new User();

  userAttributes: any = {};

  userId: number;

  constructor(
    private authService: AuthService,
    public browserNotificationService: BrowserNotificationService,
    private httpClientService: HttpClientService,
    private location: Location,
    private notyService: NotyService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService) {}

  ngOnInit() {
    this.userId = this.route.snapshot.params.id;
    this.userService.getUserShowData().then((response_json) => {
      this.userService.navbarComponent.setHomeMenu(response_json.user_login, response_json.user_id, response_json.contact_id);
    });
    this.loadUserGroups();
    this.loadOtpSettings();
    this.userService.formOptions().then((response_json) => {
      this.formOptions = response_json;
      if (this.formOptions.termination_reasons && this.formOptions.termination_reasons.length > 0) {
        let maximumCodeSize: number = 0;
        for (let terminationReason of this.formOptions.termination_reasons) if (terminationReason.code.length > maximumCodeSize) maximumCodeSize = terminationReason.code.length;
        for (let terminationReason of this.formOptions.termination_reasons) terminationReason.label = `${terminationReason.code}${Array(maximumCodeSize - terminationReason.code.length + 1).join('&nbsp;')}&nbsp;${terminationReason.label}`;
      }
    });
    if (this.userId)
      this.userService.edit(this.userId).then((response_json) => {
        this.person = response_json.person;
        this.user = response_json.user;
      });
    else
      this.userService.newUser().then((response_json) => {
        this.person = response_json.person;
        this.user = response_json.user;
      });
  }

  back() {
    this.location.back();
  }

  loadUserGroups() {
    this.authService.getCurrentUserGroups().then((response_json) => {
      this.currentUserGroups = {};
      for (let groupName of response_json.groups) this.currentUserGroups[groupName] = true;
      this.currentUserId = response_json.current_user_id;
    });
  }

  loadOtpSettings() {
    this.loadingOtpSettings = true;
    this.userService.otpSettings()
      .then(response_json => {
        this.loadingOtpSettings = false;
        this.otpSettings = response_json;
        for(let otpSource of (this.otpSettings.otp_sources || [])) {
          this.otpSettings[this.otpSourceSendKey(otpSource)] = true;
        }
        this.otpContactEmailUseChange(this.otpSettings.otp_use_contact_email);
        this.otpContactPhoneUseChange(this.otpSettings.otp_use_contact_phone);
      }).catch((err) => {
        console.error(err);
        this.notyService.notify('Error loading 2 factor auth settings', 'error');
        this.loadingOtpSettings = false;
      });
  }

  newApiKey() {
    this.creatingNewApiKey = true;
    this.userService
      .newApiKey(this.userId)
      .then((response_json) => {
        this.creatingNewApiKey = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'New API key generated', 'success');
          this.user.api_key = response_json.key;
        } else this.notyService.notify(response_json.text || 'Error generating API key', 'error');
      })
      .catch(() => {
        this.creatingNewApiKey = false;
        this.notyService.notify('Error generating API key', 'error');
      });
  }

  openLink(link: string) {
    this.router.navigateByUrl(link);
  }

  otpContactEmailUseChange(otp_use_contact_email: boolean) {
    if(otp_use_contact_email) {
      this.otpSettings.otp_email = this.otpSettings.primary_email;
    }else{
      this.otpSettings.otp_email = '';
    }
  }

  otpContactPhoneUseChange(otp_use_contact_phone: boolean) {
    if(otp_use_contact_phone) {
      this.otpSettings.otp_phone = this.otpSettings.primary_phone;
    }else{
      this.otpSettings.otp_phone = '';
    }
  }

  otpEnablementUpdated(otp_required_for_login: boolean) {
    if(otp_required_for_login && !this.otpSettings.otp_send_to_email && !this.otpSettings.otp_send_to_phone) {
      this.otpSettings.otp_send_to_email = true;
    }
  }

  save(currentForm: NgForm) {
    if (!currentForm.form.valid) {
      this.notyService.notify('Please check form', 'warning');
      return;
    }
    this.saving = true;
    this.user.id = this.userId;
    this.fields.person = this.person;
    this.fields.user = this.user;
    this.userService
      .save(this.fields)
      .then((response_json) => {
        this.saving = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Save successful', 'success');
          if (this.userId) {
            if (this.currentUserGroups.admin || this.currentUserGroups.priviledged || this.currentUserGroups.tech || this.currentUserGroups.superuser) this.router.navigateByUrl(`/users`);
            else this.router.navigateByUrl(`/`);
          }
        } else this.notyService.notify(response_json.text || 'Error saving', 'error');
        if (!this.userId)
          if (response_json.user_id && response_json.person_id) {
            this.userId = response_json.user_id;
            this.user.id = response_json.user_id;
            this.person.id = response_json.person_id;
            this.router.navigateByUrl(`/users/${this.userId}/edit`);
          }
      })
      .catch(() => {
        this.saving = false;
        this.notyService.notify('Error saving', 'error');
      });
  }

  saveOtpSettings() {
    let otpSources: Array<string> = [];
    for(let otpSource of OTP_SOURCES) {
      if(this.otpSettings[this.otpSourceSendKey(otpSource)]) {
        otpSources.push(otpSource);
      }
    }
    this.otpSettings.otp_sources = otpSources;
    if(this.validateOtpSettings()) {
      this.updatingOtpSettings = true;
      this.userService.saveOtpSettings(this.otpSettings)
        .then(response_json => {
          this.updatingOtpSettings = false;
          if(response_json.success) {
            this.notyService.notify(response_json.text || 'Updated 2 factor auth', 'success');
          }else{
            this.notyService.notify(response_json.text || 'Error updating 2 factor auth', 'error');
          }
        }).catch(() => {
          this.updatingOtpSettings = false;
        })
    }
  }

  otpSourceSendKey(otpSource: string): string {
    return `otp_send_to_${otpSource}`;
  }

  onSignatureFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (!file) {
      return;
    }

    // Validate file type
    if (file.type !== 'image/png') {
      this.notyService.notify('Please select a PNG file', 'error');
      if (this.signatureFileInput) {
        this.signatureFileInput.nativeElement.value = '';
      }
      return;
    }

    // Upload the file
    this.uploadingSignature = true;
    const formData = new FormData();
    formData.append('signature_file', file);

    this.httpClientService.post(`/users/${this.userId}/upload_signature`, formData)
      .then((response: any) => {
        this.uploadingSignature = false;
        if (response.success) {
          this.notyService.notify(response.text || 'Signature uploaded successfully', 'success');
        } else {
          this.notyService.notify(response.text || 'Error uploading signature', 'error');
        }
        // Clear the file input
        if (this.signatureFileInput) {
          this.signatureFileInput.nativeElement.value = '';
        }
      })
      .catch((error) => {
        this.uploadingSignature = false;
        this.notyService.notify('Error uploading signature', 'error');
        console.error('Signature upload error:', error);
        // Clear the file input
        if (this.signatureFileInput) {
          this.signatureFileInput.nativeElement.value = '';
        }
      });
  }

  validateOtpSettings(): boolean {
    this.otpSettingsSubmitted = true;
    if(this.otpSettings) {
      if(this.otpSettings.otp_required_for_login){
        if(!this.otpSettings.otp_sources || this.otpSettings.otp_sources.length < 1){
          this.notyService.notify('Atleast one method must be selected to receive auth code', 'error');
          return false;
        }

        let errors: Array<string> = [];
        if(!this.otpSettings.otp_use_contact_email && !this.otpSettings.otp_email){
          errors.push('Email needs to be filled if not using contact email');
        }else if(this.otpSettings.otp_use_contact_email && !this.otpSettings.primary_email){
          errors.push('Your contact does not have a primary email selected, select one in contact page and refresh this page to use it.')
        }
        if(!this.otpSettings.otp_use_contact_phone && !this.otpSettings.otp_phone){
          errors.push('Phone needs to be filled if not using contact phone');
        }else if(this.otpSettings.otp_use_contact_phone && !this.otpSettings.primary_phone){
          errors.push('Your contact does not have a primary phone selected, select one in contact page and refresh this page to use it.')
        }
        if(errors.length > 0){
          this.notyService.notify(errors.join('. '), 'error');
          return false;
        }

        return true;
      }else{
        return true;
      }
    }else {
      this.notyService.notify('Missing settings, refresh page and try please', 'error');
      return false;
    }
  }
}
